import { Link } from "react-router-dom";
import {
  Sparkles,
  Search,
  CheckSquare,
  Mail,
  Snowflake,
  PenLine,
  Rocket,
  BarChart3,
  ArrowRight,
  LifeBuoy,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { PageHeader } from "@/shared/components/PageHeader";
import { Card } from "@/shared/components/ui/card";
import { Button } from "@/shared/components/ui/button";
import { StatusText } from "@/shared/components/ui/status-chip";

/**
 * Help / user manual (global nav). A friendly, self-contained guide to the app:
 * what it does, the pipeline, a step-by-step walkthrough with in-app links, and
 * a short FAQ. No backend — pure content, styled with the design system.
 */
export function HelpPage() {
  return (
    <div className="mx-auto max-w-3xl">
      <PageHeader
        title="Help & user guide"
        footnote="Everything you need to run outreach with Growth Engine — what each screen does, the order to use them in, and answers to common questions."
      />

      {/* What is this */}
      <Card className="p-5">
        <div className="flex items-start gap-3">
          <LifeBuoy className="mt-[2px] h-[18px] w-[18px] shrink-0 text-ge-accent" strokeWidth={1.8} />
          <div>
            <p className="text-[14.5px] text-ge-primary">
              Growth Engine finds gyms and leads, screens them, and runs cold email outreach to bring
              them onto WanderFitness. You point it at a market, review who it found, then let it
              write, sequence, and send — and watch who replies.
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              <Button asChild>
                <Link to="/campaigns/new">
                  <Sparkles className="h-[14px] w-[14px]" strokeWidth={2.2} />
                  Start a campaign
                </Link>
              </Button>
              <Button asChild variant="outline">
                <Link to="/campaigns">See your campaigns</Link>
              </Button>
            </div>
          </div>
        </div>
      </Card>

      {/* The pipeline at a glance */}
      <section className="mt-8">
        <p className="ge-eyebrow">THE PIPELINE</p>
        <p className="mt-2 text-[13.5px] text-ge-secondary">
          Every campaign moves left to right through the same eight stages. The tabs inside a campaign
          follow this exact order.
        </p>
        <Card className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-2 px-4 py-3">
          {STAGES.map((s, i) => (
            <div key={s} className="flex items-center gap-2">
              <span className="font-mono text-[11.5px] text-ge-value">{s}</span>
              {i < STAGES.length - 1 && (
                <ArrowRight className="h-3 w-3 text-ge-ghost" strokeWidth={2.4} />
              )}
            </div>
          ))}
        </Card>
      </section>

      {/* Step by step */}
      <section className="mt-8">
        <p className="ge-eyebrow">STEP BY STEP</p>
        <div className="mt-3 space-y-3">
          {STEPS.map((step, i) => (
            <Step key={step.title} n={i + 1} {...step} />
          ))}
        </div>
      </section>

      {/* Global views */}
      <section className="mt-8">
        <p className="ge-eyebrow">GLOBAL VIEWS (LEFT SIDEBAR)</p>
        <p className="mt-2 text-[13.5px] text-ge-secondary">
          The sidebar shows everything across all your campaigns at once.
        </p>
        <div className="mt-3 grid gap-2 sm:grid-cols-2">
          {GLOBAL.map((g) => (
            <Link
              key={g.to}
              to={g.to}
              className="flex items-center gap-3 rounded-ge-control border border-ge-border-card px-3 py-3 hover:bg-ge-card"
            >
              <g.icon className="h-[16px] w-[16px] text-ge-accent" strokeWidth={1.8} />
              <span className="min-w-0">
                <span className="block text-[13px] font-semibold text-ge-primary">{g.label}</span>
                <span className="block text-[12px] text-ge-secondary">{g.desc}</span>
              </span>
            </Link>
          ))}
        </div>
      </section>

      {/* Data sources / connectors */}
      <section className="mt-8" data-testid="help-data-sources">
        <p className="ge-eyebrow">DATA SOURCES</p>
        <p className="mt-2 text-[13.5px] text-ge-secondary">
          Choose where a campaign gets its data on the New Campaign screen. More connectors are coming; some
          need a key before they light up (shown locked until connected).
        </p>
        <div className="mt-3 grid gap-2 sm:grid-cols-3">
          <Card className="p-4">
            <p className="text-[13px] font-semibold text-ge-primary">AI discovery</p>
            <p className="mt-1 text-[12.5px] text-ge-secondary">
              Finds real targets from your plain-language brief. Always available.
            </p>
          </Card>
          <Card className="p-4">
            <p className="text-[13px] font-semibold text-ge-primary">Google Places</p>
            <p className="mt-1 text-[12.5px] text-ge-secondary">
              Pulls real businesses from Google Places (needs a Places key).
            </p>
          </Card>
          <Card className="p-4">
            <p className="text-[13px] font-semibold text-ge-primary">AirDNA</p>
            <p className="mt-1 text-[12.5px] text-ge-secondary">
              Ranks cities/markets by opportunity and reaches short-term-rental hosts (needs an AirDNA key).
            </p>
          </Card>
        </div>
      </section>

      {/* Browsing leads */}
      <section className="mt-8" data-testid="help-leads-browsing">
        <p className="ge-eyebrow">BROWSING LEADS</p>
        <p className="mt-2 text-[13.5px] text-ge-secondary">
          Campaigns can return a lot of leads. The Leads tab (inside a campaign) and the
          <span className="font-semibold text-ge-primary"> Leads</span> directory (sidebar) share the same
          tools, so a large list stays easy to work.
        </p>
        <div className="mt-3 grid gap-2 sm:grid-cols-2">
          <Card className="p-4">
            <p className="text-[13px] font-semibold text-ge-primary">Search &amp; filter</p>
            <p className="mt-1 text-[12.5px] text-ge-secondary">
              Search by name, website, city or contact email. Filter by status (the pills, with live
              counts), source, or “verified email”, and sort by newest or rating. Everything runs on the
              server, so it works across the whole list — not just the current page.
            </p>
          </Card>
          <Card className="p-4">
            <p className="text-[13px] font-semibold text-ge-primary">Paginate</p>
            <p className="mt-1 text-[12.5px] text-ge-secondary">
              Results come in pages of 25 with a running “X–Y of total”. Prev / Next move through them
              without losing your filters.
            </p>
          </Card>
          <Card className="p-4">
            <p className="text-[13px] font-semibold text-ge-primary">Bulk approve / reject</p>
            <p className="mt-1 text-[12.5px] text-ge-secondary">
              Tick the rows you want and approve or reject the whole selection at once. Only real
              candidates and approved leads are selectable.
            </p>
          </Card>
          <Card className="p-4">
            <p className="text-[13px] font-semibold text-ge-primary">Detail drawer</p>
            <p className="mt-1 text-[12.5px] text-ge-secondary">
              Click a lead to open a drawer with everything we collected — identity, contacts, campaign
              links, the screening verdict, and the raw source data in a collapsible block. The URL
              carries the open lead, so you can share a direct link.
            </p>
          </Card>
        </div>
      </section>

      {/* AI & Brand (admin) */}
      <section className="mt-8" data-testid="help-ai-brand">
        <p className="ge-eyebrow">AI &amp; BRAND (WORKSPACE ADMIN)</p>
        <p className="mt-2 text-[13.5px] text-ge-secondary">
          Teach the AI your business so the assistant and the outreach copy sound like you — not a
          generic tool. Found under <span className="font-semibold text-ge-primary">AI &amp; Brand</span> in
          the sidebar (workspace admins only).
        </p>
        <div className="mt-3 grid gap-2 sm:grid-cols-3">
          <Card className="p-4">
            <p className="text-[13px] font-semibold text-ge-primary">Rules</p>
            <p className="mt-1 text-[12.5px] text-ge-secondary">
              What your business does, your audience, voice &amp; tone, do&apos;s and don&apos;ts, and
              banned words — always applied.
            </p>
          </Card>
          <Card className="p-4">
            <p className="text-[13px] font-semibold text-ge-primary">Skills</p>
            <p className="mt-1 text-[12.5px] text-ge-secondary">
              Reusable instruction bundles (e.g. &ldquo;cold email for boutique gyms&rdquo;) applied
              on-demand by where they&apos;re scoped.
            </p>
          </Card>
          <Card className="p-4">
            <p className="text-[13px] font-semibold text-ge-primary">Brand kit</p>
            <p className="mt-1 text-[12.5px] text-ge-secondary">
              From-name, signature, colors, logo and images — the look &amp; feel of what the AI writes
              (email now, ads later).
            </p>
          </Card>
        </div>
        <Link
          to="/ai-profile"
          className="mt-3 inline-flex items-center gap-1 text-[12.5px] font-semibold text-ge-accent hover:text-ge-accent-hover"
        >
          Open AI &amp; Brand
          <ArrowRight className="h-[13px] w-[13px]" strokeWidth={2.4} />
        </Link>
      </section>

      {/* FAQ */}
      <section className="mt-8">
        <p className="ge-eyebrow">GOOD TO KNOW</p>
        <div className="mt-3 space-y-3">
          {FAQ.map((f) => (
            <Card key={f.q} className="p-4">
              <p className="text-[13.5px] font-semibold text-ge-primary">{f.q}</p>
              <p className="mt-1 text-[13px] text-ge-secondary">{f.a}</p>
            </Card>
          ))}
        </div>
      </section>

      <p className="mt-8 text-[12px] text-ge-muted">
        Still stuck? Reach the team on the internal channel, or drop a note in the project tracker —
        we read every one.
      </p>
    </div>
  );
}

const STAGES = ["Discover", "Review", "Enrich", "Freeze", "Copy", "Sequence", "Launch", "Measure"];

interface StepData {
  title: string;
  icon: LucideIcon;
  body: string;
  to: string;
  cta: string;
}

const STEPS: StepData[] = [
  {
    title: "Create a campaign & discover targets",
    icon: Search,
    body: "A campaign is the container for everything. From “Start a campaign”, describe who you want to reach in plain language (e.g. “independent strength gyms in Austin, TX with strong reviews”), optionally set a target type, pick a data source, and run — it creates the campaign and discovers in one step, no second screen. Not sure which market? Use “Rank markets” to sort cities by opportunity and add one to your brief. Every result is screened for duplicates and already-registered targets.",
    to: "/campaigns/new",
    cta: "New campaign",
  },
  {
    title: "Review the leads",
    icon: CheckSquare,
    body: "In the campaign’s Leads tab, approve the candidates worth reaching and reject the rest. A big result set is easy to work: search by name, website, city or email; filter by status, source or “verified email”; and page through the results. Tick several rows and approve or reject them in one go. Click any lead to peek at its detail drawer, or open its full profile to see the company, every contact and their Smartlead engagement (opens, clicks, replies). Screened-out rows (duplicates, already-registered) stay visible, greyed, with the reason — you only act on real candidates.",
    to: "/leads",
    cta: "Lead directory",
  },
  {
    title: "Enrich for contact emails",
    icon: Mail,
    body: "Run enrichment on the approved leads to find and verify contact emails — plus phone and company data. Undeliverable addresses are dropped; risky ones are kept but flagged. Only verified, not-opted-out contacts become eligible to send. Need to refresh or retry a single lead? Open its detail drawer and use Re-enrich.",
    to: "/campaigns",
    cta: "Open a campaign",
  },
  {
    title: "Freeze the audience",
    icon: Snowflake,
    body: "Freezing snapshots the approved leads so later edits never change what was sent. The suppression gate re-runs here (fail-closed) — the last barrier before anyone is enrolled.",
    to: "/campaigns",
    cta: "Open a campaign",
  },
  {
    title: "Write the copy & build the sequence",
    icon: PenLine,
    body: "In the Sequences tab, generate email copy from a short prompt, preview it rendered over your real audience (it flags any unknown merge tokens), then assemble the drip steps — a template and a send delay per step.",
    to: "/sequences",
    cta: "All sequences",
  },
  {
    title: "Launch",
    icon: Rocket,
    body: "Launching pushes the sequence to the email provider and enrolls the frozen, eligible audience. You’ll see how many contacts were enrolled. From here the provider sends and reports back.",
    to: "/sequences",
    cta: "All sequences",
  },
  {
    title: "Measure & follow up",
    icon: BarChart3,
    body: "Watch performance in Analytics — the funnel from discovered to opened, unique open rate, and per-step engagement. The Outbox streams every delivery event (sent, opened, replied) so you can spot who’s interested and follow up.",
    to: "/analytics",
    cta: "Portfolio analytics",
  },
];

const GLOBAL = [
  { to: "/campaigns", icon: Sparkles, label: "Campaigns", desc: "Every campaign and its top-line metrics." },
  { to: "/discovery", icon: Search, label: "Discovery", desc: "Start a run or jump into a recent campaign." },
  { to: "/leads", icon: CheckSquare, label: "Leads", desc: "The full lead directory across campaigns." },
  { to: "/sequences", icon: PenLine, label: "Sequences", desc: "Every outreach sequence and its status." },
  { to: "/outbox", icon: Mail, label: "Outbox", desc: "Delivery events streaming back from the provider." },
  { to: "/analytics", icon: BarChart3, label: "Analytics", desc: "Portfolio performance across all campaigns." },
] satisfies Array<{ to: string; icon: LucideIcon; label: string; desc: string }>;

const FAQ = [
  {
    q: "Why is a lead greyed out and not selectable?",
    a: "It was screened out — either a duplicate of another lead or a gym already registered on WanderFitness. It stays listed with its reason so nothing is hidden, but you can’t approve it.",
  },
  {
    q: "Open rate vs. total opens — what’s the difference?",
    a: "Open rate counts unique recipients who opened at least once, so it never exceeds 100%. Total opens counts every re-open — a contact opening the same email many times is a strong interest (or hesitation) signal.",
  },
  {
    q: "How do I find one lead, or see everything we collected about it?",
    a: "Use the search box on the Leads tab or the Leads directory — it matches name, website, city and contact email — and narrow with the status/source/verified filters. Click any lead to open its detail drawer: identity, all contacts, which campaigns it’s in, the screening verdict, and the raw source data (expand the “Raw provenance” block). The open lead is in the URL, so you can copy the link and send it to a teammate.",
  },
  {
    q: "I launched but the Outbox is empty. Why?",
    a: "Delivery events arrive from the email provider by webhook after sending starts, so there’s a short delay. Once events flow, the Outbox and the per-step Analytics fill in automatically.",
  },
  {
    q: "How do I switch workspace?",
    a: "Use the workspace switcher in the top bar. Everything on screen — campaigns, leads, analytics — is scoped to the active workspace.",
  },
  {
    q: "Where does discovery get its data, and what is “Rank markets”?",
    a: "On New Campaign you pick a data source: AI discovery (always on), Google Places, or AirDNA — each is a connector, and some need an API key before they’re selectable. If you’re unsure which city to target, “Rank markets” (AirDNA) sorts cities by opportunity so you can add the best one to your brief before discovering.",
  },
  {
    q: "How do I make the AI sound like my business?",
    a: "Workspace admins open “AI & Brand” in the sidebar and fill in Rules (voice, do’s and don’ts, banned words), reusable Skills, and a Brand kit (signature, colors, logo). It shapes both the assistant and the generated cold-outreach copy — a live preview shows exactly what the AI receives. Leave it empty and the AI behaves generically, as before.",
  },
];

function Step({ n, title, icon: Icon, body, to, cta }: StepData & { n: number }) {
  return (
    <Card className="p-4" data-testid={`help-step-${n}`}>
      <div className="flex items-start gap-3">
        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-ge-accent-tint">
          <StatusText tone="accent">{n}</StatusText>
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <Icon className="h-[15px] w-[15px] text-ge-accent" strokeWidth={1.8} />
            <p className="text-[14px] font-semibold text-ge-primary">{title}</p>
          </div>
          <p className="mt-1 text-[13px] text-ge-secondary">{body}</p>
          <Link
            to={to}
            className="mt-2 inline-flex items-center gap-1 text-[12.5px] font-semibold text-ge-accent hover:text-ge-accent-hover"
          >
            {cta}
            <ArrowRight className="h-[13px] w-[13px]" strokeWidth={2.4} />
          </Link>
        </div>
      </div>
    </Card>
  );
}
